import type { DdwLens } from '../types';
import { FLEX_LEVELS, FRICTION_PHASES } from '../config';
import { fmtMoney, truncate } from '../calc';

interface Props {
  lens: DdwLens;
  onJumpToStep(step: number): void;
}

/**
 * Sticky right-rail summary visible during the Phase 1 wizard.
 * Updates live as the buyer fills in the lens. The "Solution words"
 * badge surfaces the red-flag check at a glance and lets them jump
 * back to Step 1 to revise.
 */
export function SidebarSummary({ lens, onJumpToStep }: Props) {
  const friction = FRICTION_PHASES.find((f) => f.key === lens.frictionPhase);
  const flex = FLEX_LEVELS.find((f) => f.key === lens.flexibility);
  const filledOutcomes = lens.outcomes.filter((o) => o && o.trim().length > 0);
  const hasJob = lens.jobStatement.trim().length > 0;
  const hasRedFlag = lens.redFlagWordsHit.length > 0;
  const feel = lens.emotional.feel;
  const avoid = lens.emotional.avoid;
  const hasEmo = feel.length > 0 || avoid.length > 0;

  return (
    <aside className="ddw-sidebar" aria-label="Lens summary">
      <header className="ddw-sidebar-header">
        <span className="ddw-sidebar-title">Your Lens</span>
      </header>

      <div className="ddw-sidebar-section">
        <div className="ddw-sidebar-section-label">
          <span>Job</span>
          {hasRedFlag && (
            <button
              type="button"
              className="ddw-sidebar-badge"
              onClick={() => onJumpToStep(1)}
              title="Solution-shaped language detected — click to revisit Step 1"
            >
              ! Solution words
            </button>
          )}
        </div>
        {hasJob ? (
          <div className="ddw-sidebar-section-value">{truncate(lens.jobStatement, 120)}</div>
        ) : (
          <div className="ddw-sidebar-section-empty">Not yet defined</div>
        )}
      </div>

      <div className="ddw-sidebar-section">
        <div className="ddw-sidebar-section-label">Friction phase</div>
        {friction ? (
          <div className="ddw-sidebar-section-value">{friction.label}</div>
        ) : (
          <div className="ddw-sidebar-section-empty">—</div>
        )}
      </div>

      <div className="ddw-sidebar-section">
        <div className="ddw-sidebar-section-label">Outcomes ({filledOutcomes.length}/3)</div>
        {filledOutcomes.length > 0 ? (
          <div className="ddw-sidebar-section-value">
            {filledOutcomes.map((o, i) => (
              <div key={i} style={{ marginBottom: 4 }}>
                {i + 1}. {truncate(o, 80)}
              </div>
            ))}
          </div>
        ) : (
          <div className="ddw-sidebar-section-empty">—</div>
        )}
      </div>

      <div className="ddw-sidebar-section">
        <div className="ddw-sidebar-section-label">How it should feel</div>
        {hasEmo ? (
          <div className="ddw-sidebar-section-value">
            {feel.length > 0 && (
              <div>
                <strong style={{ fontSize: 11, color: 'var(--secondary)' }}>WANT: </strong>
                {truncate(feel.join(', '), 100)}
              </div>
            )}
            {avoid.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <strong style={{ fontSize: 11, color: 'var(--warning)' }}>STOP: </strong>
                {truncate(avoid.join(', '), 100)}
              </div>
            )}
          </div>
        ) : (
          <div className="ddw-sidebar-section-empty">—</div>
        )}
      </div>

      <div className="ddw-sidebar-section">
        <div className="ddw-sidebar-section-label">Flexibility need</div>
        {flex ? (
          <div className="ddw-sidebar-section-value">{flex.label}</div>
        ) : (
          <div className="ddw-sidebar-section-empty">—</div>
        )}
      </div>

      {lens.tax.calculatedAnnual > 0 && (
        <div className="ddw-sidebar-tax">
          <div className="ddw-sidebar-tax-label">Estimated adaptation tax</div>
          <div className="ddw-sidebar-tax-value">{fmtMoney(lens.tax.calculatedAnnual)}/yr</div>
        </div>
      )}
    </aside>
  );
}
